import React, { useState, useMemo, useCallback } from 'react';
import { Dialog, DialogContent, DialogActions, List, ListItem, ListItemIcon, Checkbox, ListItemText, TextField, Grid, Typography, FormControlLabel, Box, useMediaQuery, useTheme } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import NietBeschikbaarDialog from './NietBeschikbaarDialog';
import CopyMaterialsDialog from './CopyMaterialsDialog';
import { MaterialVisual } from '../../pages/MateriaalBeheer/IconColorPicker';

const T = { primary: '#526075', primaryDim: '#465469', primaryContainer: '#d5e3fd', tertiary: '#635983', tertiaryContainer: '#dbcdff', surface: '#ffffff', surfaceLow: '#f1f4f5', surfaceHigh: '#e5e9eb', text: '#2d3335', secondary: '#556071', outline: '#767c7e', outlineVariant: '#adb3b5', emerald: '#10b981', emeraldBg: '#ecfdf5', error: '#a83836', errorBg: '#fef2f2', amber: '#f59e0b', amberBg: '#fef3c7', font: '"Manrope", system-ui, sans-serif', fontBody: '"Inter", system-ui, sans-serif', shadow: '0 1px 3px rgba(45,51,53,0.04)' };
const Icon = ({ name, size = 24, sx = {} }) => (<span className="material-symbols-outlined" style={{ fontSize: size, fontVariationSettings: "'FILL' 0, 'wght' 400", ...sx }}>{name}</span>);

const CATEGORIES = [
    'Cameras', 'Drones', 'Lens', 'Monitors', 'Statief', 'Geluid', 'Grip', 'Licht',
    'Camera Accessoires', 'Special effects', 'Studio equipment', 'Vervoer en karren'
];

const SimpleMaterialWizard = ({
    openWizard,
    handleCloseWizard,
    materialen,
    selectedMaterials,
    setSelectedMaterials,
    onSave,
    currentReservation,
    geselecteerdeDraaidag,
    reservationsData = {},
    reservations = [],
    initialCategory = null,
    materiaalsets = [],
    typeById = {},
    onOpenTypeInfo
}) => {
    const fullScreen = useMediaQuery(useTheme().breakpoints.down('sm'));
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedUnavailableMaterial, setSelectedUnavailableMaterial] = useState(null);
    const [showUnavailableDialog, setShowUnavailableDialog] = useState(false);
    const [activeTab, setActiveTab] = useState(0);
    const [selectedSet, setSelectedSet] = useState(null);
    const [sortMethod, setSortMethod] = useState('reservations');
    const [showOnlyAvailable, setShowOnlyAvailable] = useState(false);
    const [copyDialogOpen, setCopyDialogOpen] = useState(false);

    // Update selected category when dialog opens or initialCategory changes
    React.useEffect(() => {
        if (openWizard) {
            setSelectedCategory(initialCategory);
            setSearchTerm('');
        }
    }, [openWizard, initialCategory]);

    // Check if material is available
    const isMaterialAvailable = useCallback((material) => {
        if (!geselecteerdeDraaidag) return true;

        if (currentReservation && selectedMaterials.some(m => m.id === material.id)) {
            return true;
        }

        // Reparatie (niet bruikbaar) / Lange tijd afwezig → fysiek niet beschikbaar
        // ('Reparatie, wel bruikbaar' mag wél gereserveerd worden)
        const st = material.fields?.Status;
        if (st === 'Reparatie, niet bruikbaar' || st === 'Reparatie' || st === 'Lange tijd afwezig') return false;

        // "Geen reservering nodig" → altijd beschikbaar (genoeg op voorraad, geen datum-conflict)
        const rawT = material.fields?.MateriaalType;
        const tId = Array.isArray(rawT) ? rawT[0] : rawT;
        if (tId && typeById[tId]?.fields?.GeenReserveringNodig) return true;

        const reservations = reservationsData[material.id] || [];
        const draaidagStart = new Date(geselecteerdeDraaidag.fields.StartDatum || geselecteerdeDraaidag.fields.Start);
        const draaidagEnd = new Date(geselecteerdeDraaidag.fields.EindDatum || geselecteerdeDraaidag.fields.Eind);

        return !reservations.some(reservation => {
            if (reservation.reservationId === currentReservation?.id) {
                return false;
            }

            const resStart = new Date(reservation.startDate);
            const resEnd = new Date(reservation.endDate);

            return (resStart <= draaidagEnd && resEnd >= draaidagStart);
        });
    }, [reservationsData, geselecteerdeDraaidag, currentReservation, selectedMaterials, typeById]);

    const filteredMaterials = useMemo(() => {
        let filtered = materialen || [];

        // Gearchiveerde materialen/exemplaren niet tonen (tenzij al geselecteerd in deze reservering)
        const selSet = new Set((selectedMaterials || []).map(m => m.id));
        const isArchivedMat = (m) => {
            if (m.fields?.Status === 'Verkocht/gearchiveerd') return true;
            const raw = m.fields?.MateriaalType;
            const tid = Array.isArray(raw) ? raw[0] : raw;
            return !!(tid && typeById[tid]?.fields?.Gearchiveerd);
        };
        filtered = filtered.filter(m => selSet.has(m.id) || !isArchivedMat(m));

        if (searchTerm) {
            filtered = filtered.filter(m =>
                m.fields.ID?.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        if (activeTab === 0 && selectedCategory !== null) {
            filtered = filtered.filter(m => m.fields.Categorie === CATEGORIES[selectedCategory]);
        } else if (activeTab === 1 && selectedSet) {
            const setObj = materiaalsets.find(set => set.fields.ProjectType === selectedSet);
            const setMaterialIds = setObj?.fields.MaterialenFromMaterialenType || [];
            filtered = filtered.filter(m => setMaterialIds.includes(m.id));
        }

        if (showOnlyAvailable) {
            filtered = filtered.filter(m => isMaterialAvailable(m));
        }

        filtered.sort((a, b) => {
            const resA = a.fields.aantalReserveringen || 0;
            const resB = b.fields.aantalReserveringen || 0;
            return resB - resA;
        });

        return filtered;
    }, [materialen, searchTerm, activeTab, selectedCategory, selectedSet, materiaalsets, showOnlyAvailable, isMaterialAvailable, typeById, selectedMaterials]);

    const selectedIds = useMemo(() =>
        new Set(selectedMaterials.map(m => m.id)),
        [selectedMaterials]
    );

    const handleToggleMaterial = useCallback((material) => {
        if (selectedIds.has(material.id)) {
            setSelectedMaterials(prev => prev.filter(m => m.id !== material.id));
        } else {
            setSelectedMaterials(prev => [...prev, material]);
        }
    }, [selectedIds, setSelectedMaterials]);

    const handleSave = () => {
        if (onSave) {
            const startDate = geselecteerdeDraaidag?.fields?.Start || geselecteerdeDraaidag?.fields?.StartDatum;
            const endDate = geselecteerdeDraaidag?.fields?.Eind || geselecteerdeDraaidag?.fields?.EindDatum;
            onSave(selectedMaterials, startDate, endDate);
        }
    };

    const handleSelectAll = () => {
        setSelectedMaterials(prev => {
            const selectedIdsSet = new Set(prev.map(m => m.id));
            const newMaterials = filteredMaterials.filter(m => !selectedIdsSet.has(m.id));
            return [...prev, ...newMaterials];
        });
    };
    const handleDeselectAll = () => {
        setSelectedMaterials(prev => prev.filter(m => !filteredMaterials.some(fm => fm.id === m.id)));
    };

    const sortedMaterials = useMemo(() => {
        const arr = [...filteredMaterials];
        if (sortMethod === 'name') {
            arr.sort((a, b) => {
                const nameA = (a.fields.ID || '').toLowerCase();
                const nameB = (b.fields.ID || '').toLowerCase();
                return nameA.localeCompare(nameB);
            });
        } else {
            arr.sort((a, b) => {
                const resA = a.fields.aantalReserveringen || 0;
                const resB = b.fields.aantalReserveringen || 0;
                return resB - resA;
            });
        }
        return arr;
    }, [filteredMaterials, sortMethod]);

    const categorizedMaterials = useMemo(() => {
        if (activeTab !== 1 || !selectedSet) {
            return null;
        }

        return sortedMaterials.reduce((acc, material) => {
            const category = material.fields.Categorie || 'Overig';
            if (!acc[category]) {
                acc[category] = [];
            }
            acc[category].push(material);
            return acc;
        }, {});
    }, [sortedMaterials, activeTab, selectedSet]);

    const handleCopyMaterials = (materials) => {
        const existingIds = new Set(selectedMaterials.map(m => m.id));
        const newMaterials = materials.filter(m => !existingIds.has(m.id));
        setSelectedMaterials(prev => [...prev, ...newMaterials]);
    };

    if (!openWizard) return null;

    // Shared pill button style
    const pillBtn = (active) => ({
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        padding: '5px 14px', borderRadius: '999px', cursor: 'pointer', userSelect: 'none',
        fontFamily: T.fontBody, fontSize: '0.78rem', fontWeight: 600, transition: 'all 0.15s ease',
        border: active ? 'none' : `1px solid ${T.outlineVariant}`,
        background: active ? `linear-gradient(135deg, ${T.primary}, ${T.primaryDim})` : T.surface,
        color: active ? '#fff' : T.secondary,
        boxShadow: active ? T.shadow : 'none',
    });

    // Shared small action button
    const smallBtn = (active) => ({
        display: 'inline-flex', alignItems: 'center', gap: 2,
        padding: '2px 8px', borderRadius: '999px', cursor: 'pointer', userSelect: 'none',
        fontFamily: T.fontBody, fontSize: '0.7rem', fontWeight: 600, transition: 'all 0.15s ease',
        border: 'none',
        background: active ? `linear-gradient(135deg, ${T.primary}, ${T.primaryDim})` : 'transparent',
        color: active ? '#fff' : T.secondary,
    });

    // Material card render helper
    const renderMaterialCard = (material) => {
        const isSelected = selectedIds.has(material.id);
        const available = isMaterialAvailable(material);
        const rawType = material.fields?.MateriaalType;
        const typeId = Array.isArray(rawType) ? rawType[0] : rawType;
        const type = typeId ? typeById[typeId] : null;
        const noReserv = !!type?.fields?.GeenReserveringNodig;
        const _st = material.fields?.Status;
        const statusReason = (_st === 'Reparatie, niet bruikbaar' || _st === 'Reparatie') ? 'Reparatie nodig'
            : _st === 'Lange tijd afwezig' ? 'Lange tijd afwezig' : null;
        const usableRepair = _st === 'Reparatie, wel bruikbaar';

        return (
            <Grid item xs={12} sm={4} key={material.id}>
                <ListItem
                    button={available}
                    dense
                    onClick={() => { if (available) handleToggleMaterial(material); }}
                    style={{
                        backgroundColor: isSelected ? T.primaryContainer : !available ? T.surfaceLow : T.surface,
                        border: `1px solid ${isSelected ? T.primary : T.outlineVariant}`,
                        borderRadius: '0.5rem',
                        paddingTop: 4,
                        paddingBottom: 4,
                        opacity: available ? 1 : 0.55,
                        cursor: available ? 'pointer' : 'default',
                        pointerEvents: available ? 'auto' : 'none',
                        transition: 'all 0.15s ease',
                    }}
                >
                    {available && (
                        <ListItemIcon sx={{ minWidth: 32 }}>
                            <Checkbox
                                edge="start"
                                checked={isSelected}
                                tabIndex={-1}
                                disableRipple
                                sx={{
                                    color: T.outlineVariant,
                                    '&.Mui-checked': { color: T.primary },
                                }}
                            />
                        </ListItemIcon>
                    )}
                    {(type?.fields?.Icoon || type?.fields?.Kleur) && (
                        <Box sx={{ width: 22, height: 22, borderRadius: '0.35rem', flexShrink: 0, overflow: 'hidden', mr: 1 }}>
                            <MaterialVisual icon={type?.fields?.Icoon} color={type?.fields?.Kleur} fallbackIcon="inventory_2" iconSize={14} />
                        </Box>
                    )}
                    <ListItemText
                        sx={{ overflow: 'hidden' }}
                        primary={
                            <Typography noWrap sx={{ fontFamily: T.fontBody, fontSize: '0.82rem', fontWeight: 600, color: T.text }}>
                                {material.fields.ID}
                            </Typography>
                        }
                        secondary={
                            <Box component="span" sx={{ display: 'block' }}>
                                <Typography noWrap component="span" sx={{ display: 'block', fontFamily: T.fontBody, fontSize: '0.72rem', color: T.outline }}>
                                    {material.fields.Omschrijving}
                                </Typography>
                                {noReserv && (
                                    <Tooltip title="Genoeg in magazijn — dit materiaal geeft geen reserveringsconflicten">
                                        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3, mt: 0.2, px: 0.5, py: 0.1, borderRadius: '0.3rem', bgcolor: '#fef3c7', border: '1px solid #f59e0b40' }}>
                                            <span className="material-symbols-outlined" style={{ fontSize: 11, color: '#d4943a' }}>all_inclusive</span>
                                            <Typography component="span" sx={{ fontFamily: T.fontBody, fontSize: '0.55rem', fontWeight: 800, color: '#d4943a' }}>Geen reservering nodig</Typography>
                                        </Box>
                                    </Tooltip>
                                )}
                                {usableRepair && (
                                    <Tooltip title="In reparatie maar nog bruikbaar — mag gereserveerd worden">
                                        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3, mt: 0.2, ml: noReserv ? 0.5 : 0, px: 0.5, py: 0.1, borderRadius: '0.3rem', bgcolor: '#fef3c7', border: '1px solid #f59e0b40' }}>
                                            <span className="material-symbols-outlined" style={{ fontSize: 11, color: '#d4943a' }}>build</span>
                                            <Typography component="span" sx={{ fontFamily: T.fontBody, fontSize: '0.55rem', fontWeight: 800, color: '#d4943a' }}>Reparatie (bruikbaar)</Typography>
                                        </Box>
                                    </Tooltip>
                                )}
                            </Box>
                        }
                    />
                    {onOpenTypeInfo && (
                        <Tooltip title="Materiaal-info">
                            <Box component="span" onClick={(e) => { e.stopPropagation(); onOpenTypeInfo(material); }} sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, borderRadius: '0.35rem', cursor: 'pointer', pointerEvents: 'auto', color: T.outline, '&:hover': { bgcolor: T.surfaceLow, color: T.primary } }}>
                                <Icon name="info" size={15} />
                            </Box>
                        </Tooltip>
                    )}
                    {!available && (
                        <Tooltip title={statusReason ? 'Klik voor toelichting' : 'Bekijk reserveringen voor dit materiaal'}>
                            <Box
                                component="span"
                                onClick={e => {
                                    e.stopPropagation();
                                    setSelectedUnavailableMaterial(material);
                                    setShowUnavailableDialog(true);
                                }}
                                sx={{
                                    display: 'inline-flex', alignItems: 'center', gap: 0.5,
                                    ml: 1, px: 1.2, py: 0.3, borderRadius: '999px',
                                    bgcolor: T.errorBg, color: T.error,
                                    fontFamily: T.fontBody, fontSize: '0.68rem', fontWeight: 600,
                                    cursor: 'pointer', pointerEvents: 'auto', whiteSpace: 'nowrap',
                                }}
                            >
                                <Icon name={statusReason ? 'build' : 'block'} size={13} sx={{ color: T.error }} />
                                {statusReason || 'Niet beschikbaar'}
                            </Box>
                        </Tooltip>
                    )}
                </ListItem>
            </Grid>
        );
    };

    return (
        <Dialog
            open={openWizard}
            onClose={handleCloseWizard}
            maxWidth="md"
            fullWidth
            fullScreen={fullScreen}
            PaperProps={{
                sx: { height: fullScreen ? '100%' : '90vh', maxHeight: fullScreen ? '100%' : '90vh', borderRadius: fullScreen ? 0 : '0.75rem', display: 'flex', flexDirection: 'column' }
            }}
        >
            {/* Custom Dialog Title */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, pt: 2.5, pb: 1 }}>
                <Typography sx={{ fontFamily: T.font, fontSize: '1.2rem', fontWeight: 700, color: T.text }}>
                    {currentReservation ? 'Wijzig Reservering' : 'Nieuwe Reservering'}
                </Typography>
                <Box
                    onClick={handleCloseWizard}
                    sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        width: 32, height: 32, borderRadius: '0.5rem', cursor: 'pointer',
                        '&:hover': { bgcolor: T.surfaceLow },
                    }}
                >
                    <Icon name="close" size={20} sx={{ color: T.outline }} />
                </Box>
            </Box>

            <DialogContent style={{ paddingBottom: 0, paddingTop: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', flex: 1 }}>
                {/* Copy button */}
                {!currentReservation && (
                    <Box
                        onClick={() => setCopyDialogOpen(true)}
                        sx={{
                            display: 'inline-flex', alignItems: 'center', gap: 0.8,
                            px: 2, py: 0.8, mb: 1.5, borderRadius: '0.5rem', cursor: 'pointer',
                            border: `1px solid ${T.outlineVariant}`, color: T.secondary,
                            fontFamily: T.fontBody, fontSize: '0.82rem', fontWeight: 600,
                            transition: 'all 0.15s ease',
                            '&:hover': { bgcolor: T.surfaceLow, borderColor: T.primary, color: T.primary },
                        }}
                    >
                        <Icon name="content_copy" size={16} />
                        Kopieer materialen van andere draaidag
                    </Box>
                )}

                {/* Pill tab selector */}
                <Box sx={{ display: 'flex', gap: 0.5, p: 0.5, bgcolor: T.surfaceLow, borderRadius: '999px', width: 'fit-content', mx: 'auto', mb: 1.5 }}>
                    {['Categorieën', 'Materiaalsets'].map((label, idx) => (
                        <Box
                            key={label}
                            onClick={() => setActiveTab(idx)}
                            sx={{
                                px: 2.5, py: 0.7, borderRadius: '999px', cursor: 'pointer',
                                fontFamily: T.font, fontSize: '0.82rem', fontWeight: 700, userSelect: 'none',
                                transition: 'all 0.15s ease',
                                bgcolor: activeTab === idx ? T.surface : 'transparent',
                                color: activeTab === idx ? T.text : T.outline,
                                boxShadow: activeTab === idx ? T.shadow : 'none',
                            }}
                        >
                            {label}
                        </Box>
                    ))}
                </Box>

                {/* Category pills */}
                {activeTab === 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8, mt: 1, mb: 1.2 }}>
                        <Box component="span" onClick={() => setSelectedCategory(null)} sx={pillBtn(selectedCategory === null)}>
                            Alle
                        </Box>
                        {CATEGORIES.map((category, index) => (
                            <Box component="span" key={category} onClick={() => setSelectedCategory(index)} sx={pillBtn(selectedCategory === index)}>
                                {category}
                            </Box>
                        ))}
                    </Box>
                )}

                {/* Materiaalsets pills */}
                {activeTab === 1 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8, mt: 1, mb: 1.2 }}>
                        {materiaalsets.filter(set => !set.fields?.Gearchiveerd).map(set => (
                            <Box
                                component="span"
                                key={set.id}
                                onClick={() => setSelectedSet(set.fields.ProjectType)}
                                sx={{ ...pillBtn(selectedSet === set.fields.ProjectType), display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                            >
                                <Box sx={{ width: 18, height: 18, borderRadius: '0.3rem', overflow: 'hidden', flexShrink: 0 }}>
                                    <MaterialVisual icon={set.fields?.Icoon || 'backpack'} color={set.fields?.Kleur || T.tertiary} fallbackIcon="backpack" iconSize={12} />
                                </Box>
                                {set.fields.ProjectType}
                            </Box>
                        ))}
                    </Box>
                )}

                {/* Sort + select toolbar */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5, px: 0.5 }}>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {[
                            { key: 'name', label: 'Naam', icon: 'sort_by_alpha' },
                            { key: 'reservations', label: 'Meest gebruikt', icon: 'trending_up' },
                        ].map(opt => (
                            <Tooltip key={opt.key} title={`Sorteer op ${opt.label.toLowerCase()}`} arrow>
                                <Box onClick={() => setSortMethod(opt.key)} sx={{
                                    display: 'flex', alignItems: 'center', gap: 0.4,
                                    px: 1, py: 0.35, borderRadius: '0.5rem', cursor: 'pointer',
                                    bgcolor: sortMethod === opt.key ? T.primary : T.surfaceLow,
                                    color: sortMethod === opt.key ? '#fff' : T.secondary,
                                    fontFamily: T.fontBody, fontWeight: 600, fontSize: '0.68rem',
                                    transition: 'all 0.15s',
                                    '&:hover': { bgcolor: sortMethod === opt.key ? T.primaryDim : T.surfaceHigh },
                                }}>
                                    <Icon name={opt.icon} size={14} sx={{ color: sortMethod === opt.key ? '#fff' : T.outline }} />
                                    {opt.label}
                                </Box>
                            </Tooltip>
                        ))}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="Selecteer alle materialen in deze categorie" arrow>
                            <Box onClick={handleSelectAll} sx={{
                                display: 'flex', alignItems: 'center', gap: 0.4,
                                px: 1, py: 0.35, borderRadius: '0.5rem', cursor: 'pointer',
                                bgcolor: T.surfaceLow, color: T.secondary,
                                fontFamily: T.fontBody, fontWeight: 600, fontSize: '0.68rem',
                                transition: 'all 0.15s', '&:hover': { bgcolor: T.emeraldBg, color: T.emerald },
                            }}>
                                <Icon name="select_all" size={14} sx={{ color: 'inherit' }} />
                                Selecteer alles
                            </Box>
                        </Tooltip>
                        <Tooltip title="Deselecteer alle materialen" arrow>
                            <Box onClick={handleDeselectAll} sx={{
                                display: 'flex', alignItems: 'center', gap: 0.4,
                                px: 1, py: 0.35, borderRadius: '0.5rem', cursor: 'pointer',
                                bgcolor: T.surfaceLow, color: T.secondary,
                                fontFamily: T.fontBody, fontWeight: 600, fontSize: '0.68rem',
                                transition: 'all 0.15s', '&:hover': { bgcolor: T.errorBg, color: T.error },
                            }}>
                                <Icon name="deselect" size={14} sx={{ color: 'inherit' }} />
                                Deselecteer
                            </Box>
                        </Tooltip>
                    </Box>
                </Box>

                {/* Search + availability filter */}
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1 }}>
                    <TextField
                        fullWidth
                        placeholder="Zoek materiaal..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        size="small"
                        InputProps={{
                            startAdornment: <Icon name="search" size={20} sx={{ color: T.outline, mr: 4 }} />,
                            sx: {
                                borderRadius: '0.5rem', bgcolor: T.surfaceLow,
                                fontFamily: T.fontBody, fontSize: '0.85rem',
                                '& fieldset': { borderColor: T.outlineVariant },
                                '&:hover fieldset': { borderColor: T.primary },
                                '&.Mui-focused fieldset': { borderColor: T.primary },
                            },
                        }}
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={showOnlyAvailable}
                                onChange={(e) => setShowOnlyAvailable(e.target.checked)}
                                size="small"
                                sx={{ color: T.outlineVariant, '&.Mui-checked': { color: T.emerald } }}
                            />
                        }
                        label={
                            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.7rem', whiteSpace: 'nowrap', color: T.secondary }}>
                                Alleen beschikbaar
                            </Typography>
                        }
                        sx={{ ml: 0, mr: 0, flexShrink: 0 }}
                    />
                </Box>

                {/* Materials list — scrollable area */}
                <Box sx={{ mt: 1.2, flex: 1, overflow: 'auto', minHeight: 0 }}>
                    <List dense>
                        {categorizedMaterials ? (
                            Object.entries(categorizedMaterials).map(([category, materials]) => (
                                <React.Fragment key={category}>
                                    <Typography sx={{ mt: 2, ml: 2, fontFamily: T.font, fontSize: '0.95rem', fontWeight: 700, color: T.primary }}>
                                        {category}
                                    </Typography>
                                    <Grid container spacing={1}>
                                        {materials.map(renderMaterialCard)}
                                    </Grid>
                                </React.Fragment>
                            ))
                        ) : (
                            <Grid container spacing={1}>
                                {sortedMaterials.map(renderMaterialCard)}
                            </Grid>
                        )}
                    </List>
                </Box>
            </DialogContent>

            {/* Dialog Actions */}
            <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${T.surfaceHigh}` }}>
                <Typography sx={{ mr: 'auto', fontFamily: T.fontBody, fontSize: '0.82rem', fontWeight: 600, color: T.outline }}>
                    {selectedMaterials.length} materialen geselecteerd
                </Typography>
                <Box
                    onClick={handleCloseWizard}
                    sx={{
                        display: 'inline-flex', alignItems: 'center',
                        px: 2.5, py: 0.9, borderRadius: '0.5rem', cursor: 'pointer',
                        fontFamily: T.fontBody, fontSize: '0.85rem', fontWeight: 600,
                        color: T.secondary, border: `1px solid ${T.outlineVariant}`,
                        transition: 'all 0.15s ease',
                        '&:hover': { bgcolor: T.surfaceLow },
                    }}
                >
                    Annuleren
                </Box>
                <Box
                    onClick={handleSave}
                    sx={{
                        display: 'inline-flex', alignItems: 'center', gap: 0.8,
                        px: 2.5, py: 0.9, borderRadius: '0.5rem', cursor: 'pointer',
                        fontFamily: T.fontBody, fontSize: '0.85rem', fontWeight: 600,
                        color: '#fff', background: `linear-gradient(135deg, ${T.primary}, ${T.primaryDim})`,
                        boxShadow: T.shadow, transition: 'all 0.15s ease',
                        '&:hover': { opacity: 0.9 },
                    }}
                >
                    <Icon name="save" size={18} sx={{ color: '#fff' }} />
                    Opslaan
                </Box>
            </DialogActions>

            <NietBeschikbaarDialog
                open={showUnavailableDialog}
                onClose={() => setShowUnavailableDialog(false)}
                statusReason={(() => {
                    const st = selectedUnavailableMaterial?.fields?.Status;
                    if (st === 'Reparatie, niet bruikbaar' || st === 'Reparatie') return 'Dit exemplaar is in reparatie (niet bruikbaar) en daarom niet reserveerbaar.';
                    if (st === 'Lange tijd afwezig') return 'Dit exemplaar is lange tijd afwezig en daarom niet reserveerbaar.';
                    return null;
                })()}
                materialName={selectedUnavailableMaterial?.fields?.Naam || selectedUnavailableMaterial?.fields?.ID || ''}
                requestedStart={geselecteerdeDraaidag?.fields?.StartDatum || geselecteerdeDraaidag?.fields?.Start}
                requestedEnd={geselecteerdeDraaidag?.fields?.EindDatum || geselecteerdeDraaidag?.fields?.Eind}
                extraDagVoor={currentReservation?.fields?.ExtraDagVoor || false}
                extraDagNa={currentReservation?.fields?.ExtraDagNa || false}
                reservations={selectedUnavailableMaterial ?
                    (() => {
                        // Group by reservationId to merge per-day entries into date ranges
                        const byId = {};
                        (reservationsData[selectedUnavailableMaterial.id] || [])
                            .filter(res => res.reservationId !== currentReservation?.id)
                            .forEach(res => {
                                const key = res.reservationId || res.draaidagNaam || 'unknown';
                                if (!byId[key]) {
                                    byId[key] = { draaidagNaam: res.draaidagNaam, startDatum: new Date(res.startDate), eindDatum: new Date(res.endDate), extraDagVoor: res.extraDagVoor || false, extraDagNa: res.extraDagNa || false, draaidagStart: res.draaidagStart, draaidagEnd: res.draaidagEnd };
                                } else {
                                    const s = new Date(res.startDate);
                                    const e = new Date(res.endDate);
                                    if (s < byId[key].startDatum) byId[key].startDatum = s;
                                    if (e > byId[key].eindDatum) byId[key].eindDatum = e;
                                    if (res.extraDagVoor) byId[key].extraDagVoor = true;
                                    if (res.extraDagNa) byId[key].extraDagNa = true;
                                }
                            });
                        return Object.values(byId);
                    })() : []
                }
            />
            <CopyMaterialsDialog
                open={copyDialogOpen}
                onClose={() => setCopyDialogOpen(false)}
                currentDraaidag={geselecteerdeDraaidag}
                onCopy={handleCopyMaterials}
                reservations={reservations}
            />
        </Dialog>
    );
};

export default SimpleMaterialWizard;
